import { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import { getConstraints, type FurnitureType } from '~/config/furnitureConstraints'
import {
  WARDROBE_TEMPLATES,
  WardrobeTemplateId,
} from '~/config/wardrobeTemplates'
import { calculateWardrobeColumnLayout } from '~/utils/wardrobeColumnLayout'
import { decodeWardrobeColumnConfigs } from '~/utils/wardrobeColumnConfigUrl'
import type { WardrobeColumnConfiguration } from '~/types/wardrobeConfigurationTypes'
import { computeWardrobeParts } from '~/utils/wardrobeParts'
import { computeStandParts } from '~/utils/standParts'
import { computeWorkTableParts } from '~/utils/workTableParts'
import type { BuildPricingQuoteArgs } from '~/utils/pricingQuote'
import {
  getColumnConfigTypes,
  type CatalogPreset,
} from '~/presets/catalogPresets'

/** Backend SKU for a preset, or null when the type has no pricing endpoint. */
export function catalogSku(preset: CatalogPreset): string | null {
  switch (preset.type) {
    case 'wardrobe':
      return preset.collection === 'new-collection'
        ? 'wardrobe-new-collection'
        : 'wardrobe'
    case 'stand':
    case 'tv-stand':
    case 'bedside':
    case 'work-table':
      return preset.type
    // 'table' (and disabled 'rack') have no /api/pricing/quote integration.
    default:
      return null
  }
}

/** Map the preset's URL opening token to the OpeningType the quote expects. */
function resolveOpeningType(preset: CatalogPreset): OpeningType {
  const o = preset.openingType
  if (preset.type === 'wardrobe') {
    // Mirrors wardrobeConfigFactory (defaultHandle = ProfileHandleLong).
    switch (o) {
      case 'profile':
        return OpeningType.ProfileHandle
      case 'profile-long':
        return OpeningType.ProfileHandleLong
      case 'round':
        return OpeningType.RoundHandle
      case 'new-collection-handle':
        return OpeningType.NewCollectionHandle
      case 'push':
        return OpeningType.Push
      default:
        return OpeningType.ProfileHandleLong
    }
  }
  // Mirrors stand/tv-stand/bedside config (default = Push).
  if (o === 'profile') return OpeningType.ProfileHandle
  if (o === 'round') return OpeningType.RoundHandle
  return OpeningType.Push
}

function plintHeightOf(preset: CatalogPreset): number {
  return (
    preset.plintHeight ??
    getConstraints(preset.type as FurnitureType).dimensions.plintHeight.default
  )
}

/**
 * Reproduce, for a wardrobe preset, the { columns, columnWidths,
 * columnConfigurations } the configurator hands to WardrobePartsProvider — using
 * the same pure utils, so the catalog quote matches the product page exactly.
 */
function deriveWardrobeColumnConfig(preset: CatalogPreset): {
  columns: number
  columnWidths: number[]
  columnConfigurations: WardrobeColumnConfiguration[]
} {
  const isNewCollection = preset.collection === 'new-collection'
  const layout = calculateWardrobeColumnLayout(
    preset.dimensions.width,
    preset.isMirrored ?? false,
    isNewCollection
  )
  const totalHeight = preset.dimensions.height - plintHeightOf(preset)
  const decoded = preset.wardrobeCfg
    ? decodeWardrobeColumnConfigs(preset.wardrobeCfg)
    : []

  const columnConfigurations: WardrobeColumnConfiguration[] =
    layout.columnWidths.map((colWidth, index) => {
      const cfg = decoded[index] ?? {
        templateId: WardrobeTemplateId.FULL_HANGING_WITH_1_SHELF,
        hasDoor: true,
      }
      const template = WARDROBE_TEMPLATES[cfg.templateId as WardrobeTemplateId]
      return {
        zones: template?.zones ?? [],
        totalHeight,
        doorType: colWidth > 60 ? 'split' : 'single',
        doorOpeningSide: layout.doorOpeningSides[index],
        templateId: cfg.templateId,
        hasDoor: cfg.hasDoor,
      }
    })

  return {
    columns: layout.columnCount,
    columnWidths: layout.columnWidths,
    columnConfigurations,
  }
}

/**
 * Build the pricing-quote args for a catalog preset + selected colour's
 * chipboard. Returns null for types without a quote endpoint (table). Each
 * branch mirrors the matching product page's *QuoteProvider so the catalog price
 * equals what the configurator shows.
 */
export function buildCatalogQuoteArgs(
  preset: CatalogPreset,
  chipboardId: string | undefined
): BuildPricingQuoteArgs | null {
  const sku = catalogSku(preset)
  if (!sku) return null

  const { width, height, depth } = preset.dimensions
  const openingType = resolveOpeningType(preset)

  if (preset.type === 'wardrobe') {
    const { columns, columnWidths, columnConfigurations } =
      deriveWardrobeColumnConfig(preset)
    const parts = computeWardrobeParts({
      width,
      height,
      depth,
      plintHeight: plintHeightOf(preset),
      columns,
      columnWidths,
      openingType,
      isMirrored: preset.isMirrored ?? false,
      columnConfigurations,
    })
    const doorsNumber = parts.doors.reduce(
      (n, d) => n + (d.isHandle ? 0 : d.quantity),
      0
    )
    return {
      sku,
      chipboardId,
      width,
      height,
      depth,
      handleType: openingType,
      doorsNumber,
      sections: columns,
      parts,
      columnConfigurations,
      columnWidths,
    }
  }

  if (preset.type === 'work-table') {
    const hasDrawers = (preset.drawers ?? 0) > 0
    const { panels } = computeWorkTableParts({
      width,
      height,
      depth,
      hasDrawers,
      drawerCount: preset.drawers ?? 0,
    })
    return {
      sku,
      chipboardId,
      width,
      height,
      depth,
      handleType: 'push',
      doorsNumber: 0,
      sections: 1,
      parts: panels,
      zones: [],
    }
  }

  // stand | tv-stand | bedside
  const columns = preset.columns ?? 1
  const { panels, doorsNumber, zones } = computeStandParts({
    width,
    height,
    depth,
    plintHeight: plintHeightOf(preset),
    columns,
    sections: columns,
    columnConfigurations: getColumnConfigTypes(preset),
  })
  return {
    sku,
    chipboardId,
    width,
    height,
    depth,
    handleType: openingType,
    doorsNumber,
    sections: columns,
    parts: panels,
    zones,
  }
}